/**
 * Buyer Feed Page
 * View posts from followed sellers
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { postsAPI, followsAPI } from '../../services/api';
import {
  Loader2,
  Rss,
  Calendar,
  Eye,
  ExternalLink,
  Building2,
  Milestone,
  Link2,
  DollarSign,
  FlaskConical,
  FileCheck,
  Users,
  MessageSquare,
  MapPin,
  ArrowRight,
} from 'lucide-react';

const POST_TYPE_CONFIG = {
  pipeline_milestone: { label: 'Pipeline Milestone', icon: Milestone, color: 'purple' },
  partnership_news: { label: 'Partnership', icon: Link2, color: 'blue' },
  fundraising: { label: 'Fundraising', icon: DollarSign, color: 'green' },
  clinical_data: { label: 'Clinical Data', icon: FlaskConical, color: 'teal' },
  regulatory: { label: 'Regulatory', icon: FileCheck, color: 'orange' },
  team_update: { label: 'Team Update', icon: Users, color: 'indigo' },
  conference: { label: 'Conference', icon: Calendar, color: 'pink' },
  general: { label: 'General', icon: MessageSquare, color: 'gray' },
};

const getTypeConfig = (type) => POST_TYPE_CONFIG[type] || POST_TYPE_CONFIG.general;

const colorClasses = {
  purple: 'bg-purple-100 text-purple-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  teal: 'bg-teal-100 text-teal-700',
  orange: 'bg-orange-100 text-orange-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  pink: 'bg-pink-100 text-pink-700',
  gray: 'bg-gray-100 text-gray-600',
};

export default function Feed() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [followedCount, setFollowedCount] = useState(0);
  const [expandedPost, setExpandedPost] = useState(null);

  const buyerId = userProfile?.buyer_id;

  useEffect(() => {
    if (buyerId) {
      loadFeed();
      loadFollowedCount();
    } else {
      setLoading(false);
    }
  }, [buyerId]);

  const loadFeed = async () => {
    try {
      const response = await postsAPI.getBuyerFeed({ limit: 50 });
      setPosts(response.data?.data?.posts || []);
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowedCount = async () => {
    try {
      const response = await followsAPI.getFollowed();
      setFollowedCount(response.data?.data?.length || 0);
    } catch (error) {
      console.debug('Failed to load followed count:', error);
    }
  };

  const handlePostClick = async (post) => {
    // Track the view
    try {
      await postsAPI.trackView(post.id);
    } catch (error) {
      console.debug('Failed to track view:', error);
    }

    // Navigate to company detail
    navigate(`/buyer/company/${post.seller_id}`);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seller Feed</h1>
          <p className="text-gray-600">
            Updates from {followedCount} companies you follow
          </p>
        </div>
        <Link
          to="/buyer/search"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
        >
          Find More Sellers
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Feed */}
      {posts.length > 0 ? (
        <div className="space-y-4">
          {posts.map(post => {
            const config = getTypeConfig(post.post_type);
            const Icon = config.icon;
            const isExpanded = expandedPost === post.id;

            return (
              <div
                key={post.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Company Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-3">
                    {post.logo_url ? (
                      <img
                        src={post.logo_url}
                        alt={post.company_name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                        {post.company_name?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/buyer/company/${post.seller_id}`}
                        className="font-semibold text-gray-900 hover:text-blue-600"
                      >
                        {post.company_name}
                      </Link>
                      {post.headquarters && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {post.headquarters}
                        </p>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {formatDate(post.published_at)}
                    </span>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6">
                  {/* Type Badge */}
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full mb-3 ${colorClasses[config.color]}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {config.label}
                  </span>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {post.title}
                  </h3>

                  {/* Content */}
                  <p className={`text-gray-600 ${isExpanded ? '' : 'line-clamp-3'}`}>
                    {post.content}
                  </p>

                  {post.content.length > 200 && (
                    <button
                      onClick={() => setExpandedPost(isExpanded ? null : post.id)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2"
                    >
                      {isExpanded ? 'Show less' : 'Read more'}
                    </button>
                  )}

                  {/* External Link */}
                  {post.external_url && (
                    <a
                      href={post.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-3"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Read more
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {post.view_count || 0} views
                    </span>
                  </div>
                  <button
                    onClick={() => handlePostClick(post)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    View Company Profile
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Rss className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No updates yet</h3>
          <p className="text-gray-600 mb-6">
            Follow biotech companies to see their updates here.
          </p>
          <Link
            to="/buyer/search"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Discover Companies
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      )}
    </div>
  );
}
